import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { router } from 'expo-router'
import { Button } from '@/src/common/components/Button'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames } from '@/src/common/theme'
import { useEffect, useState } from 'react'
import { randomUUID } from '@/src/common/utils/randomUUID'
import * as Hooks from '../hooks'
import * as Stores from '../stores'
import * as Statsig from '../statsig'
import { sentryCaptureException } from '@/src/common/hooks/useLogError'

const FALLBACK_DOLLARS_PER_POINT_CONVERSION = 0.006

export function PointsToStatementCredit({
  cardAccountId,
}: {
  cardAccountId: (typeof Stores.PointsForStatementCreditApi.endpoints)['GET /card/accounts/:cardAccountId']['Types']['QueryArg']['params']['cardAccountId']
}) {
  const Alert = useAlert(PointsToStatementCredit.name)

  const {
    setPointsToRedeem,
    setCreditAmount,
    setConversionRate,
    setRemainingPoints,
    setIdempotencyKey,
    clear,
  } = Hooks.usePointsToStatementCredit()

  const initialAmount = Hooks.usePointsToStatementCreditCreditAmount()
  const [pointsForStatementCredit, setPointsForStatementCredit] = useState<number>(initialAmount)

  const dynamicConfigResult = Statsig.usePointsToStatementCreditDynamicConfig()

  const dynamicConfigValue =
    dynamicConfigResult.loadingStatus === 'Ready' ? dynamicConfigResult.dynamicConfig.value : null

  let dollarsPerPointConversion = FALLBACK_DOLLARS_PER_POINT_CONVERSION
  if (dynamicConfigValue) {
    try {
      const parsedConfig = Statsig.PointsToStatementCreditSchema.parse(dynamicConfigValue)
      dollarsPerPointConversion = parsedConfig.statement_credit_cents_per_point
    } catch (error) {
      sentryCaptureException('Failed to parse statement credit config:', error)
    }
  }

  const pointsToRedeem = Math.floor(pointsForStatementCredit / dollarsPerPointConversion)

  const { queryLoading, pointsBalance, currentDollarBalance, maxDollarValue } =
    Hooks.usePlatformPointsRedemption({
      dollarsPerPointConversion,
      creditAmount: pointsForStatementCredit,
      cardAccountId,
    })

  function handleContinue() {
    if (queryLoading) {
      Alert.alert('Card information is loading', 'Please wait a moment and try again.')
      return
    }

    // check users has a balance over $1, and that they're attempting to redeem at least $1 worth of points
    if (
      !currentDollarBalance ||
      currentDollarBalance === 'loading' ||
      currentDollarBalance < 1 ||
      pointsForStatementCredit < 1
    ) {
      Alert.alert(
        'Select Credit Amount',
        'You must have a Current Balance of at least $1.00 to redeem Mesa Points for a statement credit on your Mesa Card. The minimum redemption amount for a Statement Credit is $1.00.',
      )
      return
    }

    if (pointsBalance === 'loading') {
      Alert.alert('Points balance is loading', 'Please wait a moment and try again.')
      return
    }

    // check user has more points than they're trying to redeem
    if (pointsToRedeem > pointsBalance) {
      Alert.alert(
        'Insufficient Mesa Points',
        'You do not have the required number of Mesa Points for the requested Credit amount at this time. Please select a different Credit amount in order to proceed with redemption.',
      )
      return
    }

    setPointsToRedeem(pointsToRedeem.toString())
    setConversionRate(dollarsPerPointConversion.toString())
    setCreditAmount(pointsForStatementCredit.toString())
    setRemainingPoints((pointsBalance - pointsToRedeem).toString())

    router.push({
      pathname: '/main/points-to-statement-credit/redemption-confirmation',
      params: { creditAmount: pointsForStatementCredit.toString() },
    })
  }

  useEffect(() => {
    clear()
    setIdempotencyKey(randomUUID())
  }, [clear, setIdempotencyKey])

  return (
    <StandaloneWrapper
      screen="/main/points-to-statement-credit/statement-credit"
      title="Statement Credit"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <DesignSystem.PointsRedemption.PointConversionInfoCard
        pointsBalance={pointsBalance}
        dollarsPerPointConversion={dollarsPerPointConversion}
        currentBalance={currentDollarBalance}
      />

      <DesignSystem.PointsRedemption.SelectCreditAmount
        maxDollarValue={maxDollarValue}
        pointsToRedeem={pointsToRedeem}
        creditAmount={pointsForStatementCredit}
        setCreditAmount={setPointsForStatementCredit}
      />

      <Button
        onPress={handleContinue}
        disabled={
          !pointsToRedeem ||
          !pointsForStatementCredit ||
          pointsBalance === 'loading' ||
          queryLoading
        }
        title="Continue"
        style={{ marginBottom: 40 }}
      />
    </StandaloneWrapper>
  )
}
